import styled from "styled-components";
import media from "styled-media-query";
import { ChallengeScheme } from "client-v2";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import "dayjs/locale/es";

import {
	Text,
	Button,
	Color,
	ColorV2,
	FAQs,
} from "@adoptaunabuelo/react-components";
import PriceBreakdownCell from "../../../components/cell/PriceBreakdownCell";
import { Cake, PartyPopper } from "lucide-react";
import { useState } from "react";
import ReliefModal from "../../../components/modal/ReliefModal";
import ProtectedDonationModal from "../../../components/modal/ProtectedDonationModal";
import DonationShield from "../../../assets/images/icon/DonationShield";
import { useSelector } from "react-redux";
import { State } from "../../../redux";

const Container = styled.div`
	display: flex;
	flex: 1;
	flex-direction: column;
`;
const Section = styled.div`
	display: flex;
	flex: 1;
	flex-direction: column;
	margin-top: 48px;
`;
const MobileSection = styled.div`
	display: none;
	flex: 1;
	flex-direction: column;
	margin-top: 48px;
	${media.lessThan("medium")`
        display: flex;
    `}
`;
const HowWork = styled.div`
	display: flex;
	flex-direction: column;
	gap: 24px;
`;
const Row = styled.div`
	display: flex;
	flex: 1;
	flex-direction: row;
`;
const Col = styled.div`
	display: flex;
	flex: 1;
	flex-direction: column;
`;
const Numbers = styled(Text)`
	display: flex;
	align-items: center;
	justify-content: center;
	border: 2px solid ${ColorV2.border.neutralHard};
	margin-right: 16px !important;
	height: 24px;
	width: 24px;
	border-radius: 30px;
`;
const Cell = styled.div`
	display: flex;
	flex-direction: row;
	align-items: center;
	margin-bottom: 18px;
`;
const Image = styled.img`
	height: 40px;
	width: 40px;
	border-radius: 40px;
	margin-right: 12px;
`;
const Icon = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	height: 44px;
	width: 44px;
	border-radius: 8px;
	background-color: ${ColorV2.surface.background};
`;
const Cell2 = styled.div`
	display: flex;
	flex: 1;
	flex-direction: row;
	padding: 12px 16px;
	border-radius: 16px;
	gap: 12px;
	cursor: pointer;
`;
const Cell2Icon = styled.div`
	width: 40px;
	height: 40px;
`;

const Info = (props: Props) => {
	dayjs.extend(relativeTime);
	dayjs.locale("es");
	const endDate = dayjs(props.challenge.endDate.iso);
	const today = dayjs();

	const isMobile: boolean = useSelector(
		(state: State) => state.screen.isMobile
	);

	const [showReliefModal, setShowReliefModal] = useState(false);
	const [showProtectedModal, setShowProtectedModal] = useState(false);

	const howWorks = [
		{
			title: "Escribe tu felicitación",
			subtitle:
				"Incluye en ella tus mejores deseos, palabras bonitas y todos los piropos que se merece. Nosotros nos encargamos de hacérsela llegar el día de su cumpleaños.",
		},
		{
			title: "Personaliza tu felicitación",
			subtitle:
				"Podrás elegir el tipo de letra y si quieres que aparezca el remitente para que tu felicitación sea única.",
			new: true,
		},
		{
			title: "Participa en los regalos",
			subtitle:
				"Un pajarito nos ha chivado los regalos que le haría mucha ilusión recibir. Colabora con el bote para que podamos comprarlos.",
		},
		{
			title: "¡Hora de celebrar!",
			subtitle:
				"Organizaremos una fiesta para celebrar su cumpleaños y hacerle llegar todas vuestras felicitaciones y regalos. ¡Os lo contaremos todo por nuestras redes!",
		},
	];

	const renderCell = (data: {
		icon: React.ComponentType<any>;
		iconColor?: string;
		iconBackground?: string;
		title: string;
		subtitle?: string;
		style?: {
			margin?: number;
			fontSize?: number;
			textColor?: string;
			fontWeight?: number;
		};
	}) => (
		<Row
			style={{
				gap: 12,
				marginBottom: data.style?.margin ? data.style.margin : 0,
				alignItems: data.subtitle ? "flex-start" : "center",
			}}
		>
			<Icon
				style={{
					backgroundColor: data.iconBackground
						? data.iconBackground
						: ColorV2.surface.background,
				}}
			>
				<data.icon
					style={{
						minHeight: 24,
						minWidth: 24,
					}}
					height={24}
					width={24}
					color={
						data.iconColor
							? data.iconColor
							: ColorV2.text.neutralHard
					}
				/>
			</Icon>
			<Col
				style={{
					flex: 1,
				}}
			>
				<Text
					type="p"
					weight="medium"
					style={{
						fontSize: data.style?.fontSize,
						fontWeight: data.style?.fontWeight,
						color: data.style?.textColor,
					}}
				>
					{data.title}
				</Text>
				{data.subtitle && (
					<Text
						type="p"
						style={{
							color: ColorV2.text.neutralMedium,
						}}
					>
						{data.subtitle}
					</Text>
				)}
			</Col>
		</Row>
	);

	const renderCell2 = (data: {
		title: string;
		subtitle: string;
		background?: string;
		Icon: React.ElementType<any>;
		onClick: () => void;
	}) => (
		<Cell2 style={{ background: data.background }} onClick={data.onClick}>
			<Text type="p2" style={{ flex: 1 }}>
				<span style={{ fontWeight: 600, textDecoration: "underline" }}>
					{data.title}
				</span>{" "}
				{data.subtitle}
			</Text>
			<Cell2Icon>
				<data.Icon />
			</Cell2Icon>
		</Cell2>
	);

	return (
		<Container>
			<ReliefModal
				isVisible={showReliefModal}
				price={50}
				charge={"one"}
				onClose={() => setShowReliefModal(false)}
			/>
			<ProtectedDonationModal
				type={"birthday"}
				isVisible={showProtectedModal}
				onClose={() => setShowProtectedModal(false)}
			/>
			<MobileSection style={{ marginTop: 24 }}>
				{renderCell({
					icon: PartyPopper,
					title: props.amount + 18 + " felicitaciones",
					iconColor: ColorV2.text.primary,
					iconBackground: ColorV2.border.primarySoft,
					style: {
						margin: 12,
						fontSize: 24,
						fontWeight: 600,
						textColor: ColorV2.text.primary,
					},
				})}
				{renderCell({
					icon: Cake,
					title:
						"¡Quedan " + (endDate as any).from(today, true) + "!",
					subtitle:
						"El cumpleaños es el " +
						dayjs(endDate).format("DD MMMM"),
				})}
			</MobileSection>
			<Section>
				<Text type="h4" weight="semibold" style={{ marginBottom: 24 }}>
					¡Estamos de cumpleaños!
				</Text>
				<Text type="p" style={{ whiteSpace: "pre-wrap" }}>
					{props.challenge.history}
				</Text>
			</Section>
			<Section>
				<Text type="h4" weight="semibold" style={{ marginBottom: 24 }}>
					Cómo funciona
				</Text>
				<HowWork>
					{howWorks.map((item, index) => (
						<Row key={"how-work-" + index}>
							<Numbers type="p" weight="semibold">
								{index + 1}
							</Numbers>
							<Col>
								<Text type="p" weight="medium">
									{item.title}
									{item.new && (
										<span
											style={{
												color: ColorV2.text.primary,
												marginLeft: 6,
											}}
										>
											¡novedad!
										</span>
									)}
								</Text>
								<Text
									type="p2"
									style={{
										color: ColorV2.text.neutralMedium,
									}}
								>
									{item.subtitle}
								</Text>
							</Col>
						</Row>
					))}
				</HowWork>
			</Section>
			<Section>
				<Text type="h4" weight="semibold" style={{ marginBottom: 8 }}>
					Con estos regalos acertamos fijo
				</Text>
				<Text
					type="p"
					style={{ marginBottom: 32, color: Color.text.high }}
				>
					Queremos que sea un cumpleaños muy especial, así que vamos a
					enviarle regalos de parte de todos. Tu aportación irá a un
					bote común para poder comprarlos.
				</Text>
				<PriceBreakdownCell
					title={"Con tu colaboración podremos regarle:"}
					options={props.challenge.distribution}
				/>
				<Button
					style={{
						width: "fit-content",
						marginTop: 24,
						color: ColorV2.text.neutralHard,
						borderColor: ColorV2.text.neutralHard,
					}}
					design={"secondary"}
					onClick={() => props.goToPayment()}
				>
					Quiero colaborar con los regalos
				</Button>
			</Section>
			<Section>
				<Text type="h4" weight="semibold" style={{ marginBottom: 8 }}>
					Las ventajas de colaborar
				</Text>
				<Text type="p" style={{ marginBottom: 32 }}>
					Queremos que colaborar con Adopta Un Abuelo sea fácil para
					ti, por eso te garantizamos:
				</Text>
				<Row
					style={{
						gap: 16,
						flexDirection: isMobile ? "column" : "row",
					}}
				>
					{renderCell2({
						title: "Donación protegida.",
						subtitle:
							"Tu pago es seguro con cifrado SSL y se destina íntegramente al cumpleaños de Dolores.",
						background: ColorV2.border.primarySoft,
						Icon: () => <DonationShield size="large" />,
						onClick: () => setShowProtectedModal(true),
					})}
					{renderCell2({
						title: "Beneficios fiscales.",
						subtitle:
							"Puedes deducirte tu donación en la próxima Declaración de la Renta.",
						background: ColorV2.surface.secondarySoft,
						Icon: () => (
							<img
								style={{ width: 40, height: 40 }}
								src={require("../../../assets/images/icon/coins.webp")}
							/>
						),
						onClick: () => setShowReliefModal(true),
					})}
				</Row>
			</Section>
			<Section>
				<Text type="h4" weight="semibold" style={{ marginBottom: 24 }}>
					Últimas felicitaciones
				</Text>
				{props.supporters.map((item, index) => (
					<Cell key={"supporter-" + index}>
						<Image
							src={
								item.user.image
									? item.user.image.url
									: require("../../../assets/images/icon/user_icon.png")
							}
						/>
						<Col>
							<Text type="p" weight="medium">
								{item.user.name}
							</Text>
							<Text
								type="p2"
								style={{ color: ColorV2.text.neutralMedium }}
							>
								{item.letter
									? "Envió su felicitación"
									: "Colaboró"}{" "}
								{(dayjs(item.createdAt) as any).fromNow()}
							</Text>
						</Col>
					</Cell>
				))}
			</Section>
			<Section id="faqs">
				<Text type="h4" weight="semibold" style={{ marginBottom: 24 }}>
					Preguntas frecuentes
				</Text>
				<FAQs
					options={[
						{
							id: "item-1",
							title: "¿Es obligatorio colaborar con los regalos?",
							description:
								"No, puedes únicamente enviar tu felicitación.",
						},
						{
							id: "item-2",
							title: "¿Qué se hace con el dinero recaudado?",
							description:
								"Se destina de forma íntegra a los regalos de cumpleaños.",
						},
						{
							id: "item-3",
							title: "¿Qué ocurre si se recauda más dinero del objetivo?",
							description:
								"El importe extra recaudado será destinado íntegramente a los programas de acompañamiento a personas mayores en situación de soledad no deseada que desarrollamos desde Adopta Un Abuelo. Por cada 1,5€ recaudado se realiza una hora de acompañamiento.",
						},
						{
							id: "item-4",
							title: "¿Hay otra forma de ayudar que no sea con dinero?",
							description:
								"Puedes ayudarnos compartiendo y dando difusión a este cumpleaños o puedes participar como voluntario descargando nuestra app.",
						},
						{
							id: "item-5",
							title: "¿Puedo asistir al cumpleaños?",
							description:
								"Por supuesto que sí, ¡cuantos más, mejor! Apúntate en el evento del cumpleaños.",
						},
						{
							id: "item-6",
							title: "¿Leerá mi felicitación?",
							description:
								"Sí, le entregamos todas las felicitaciones.",
						},
					]}
				/>
			</Section>
		</Container>
	);
};
export default Info;
export interface Props {
	challenge: ChallengeScheme;
	supporters: {
		user: any;
		amount: number;
		createdAt: Date;
		letter: boolean;
	}[];
	amount: number;
	goToPayment: () => void;
}
